import { normalizeVehicleType } from './stateRequirements';

// DMV: the same quote printed a different Vehicle Type on every surface. Three
// separate code paths were deciding the label:
//
//   Fees PDF / Fees output  VEHICLE_TYPE[vehicleType] — the fees dropdown's own
//                           display table, where 'Light Truck' -> "Pickup Truck".
//   Docs PDF / Docs output  docsFormData.vehicleType echoed raw, so whatever
//                           string the docs dropdown or the VIN decode happened
//                           to leave in redux. The decode maps 'Pickup Truck'
//                           -> 'Truck', which is why VIN 1FTPX18L2YK117378 read
//                           "Truck" on Docs and "Pickup Truck" on Fees.
//   Bot (both PDFs)         the ATC code off the request
//                           (fees/generate_fees_html.py VEHICLE_TYPE and
//                           docs/generate_docs_html.py VEHICLE_TYPE_MAP).
//
// Only the code off the request describes the quote that was actually returned,
// so resolve the label the way the request was built — form value -> ATC code ->
// label — and let every online surface share this one table.
//
// The label for LT is "Truck", matching what the Docs surfaces already showed
// for a VIN-decoded pickup. Both forms send `VEHICLE_TYPE_MAPPER[vehicleType]`
// as <VehicleType>, and that mapper collapses 'Light Truck' AND 'Truck' onto LT,
// so either selection is an LT quote and both now read "Truck" everywhere. The
// bot's tables (fees/docs/refi_* generate_*_html.py) carry the same wording, so
// online and bot PDFs agree.
//
// Consequence worth knowing: the Fees dropdown still words 'Light Truck' as
// "Pickup Truck", and picking it yields "Truck" on the output and the PDF,
// because LT is what went to ATC. Giving heavy trucks a label distinct from
// pickups means giving them their own code (TR) in VEHICLE_TYPE_MAPPER first,
// which changes the quote and is the separate split described in
// ATC_Flask/fees/handler.py's VEHICLE_TYPE_BUCKETS caveat.

// ATC legend codes -> the long names the screens and PDFs print.
//
// Byte-identical to the bot's tables. 'TR' is carried even though the frontend
// never sends it, so a payload replayed from the bot side still renders; it
// shares LT's label because no online surface distinguishes a pickup from a
// heavy truck today.
export const ATC_VEHICLE_TYPE_LABEL = {
  PV: 'Passenger Vehicle',
  LT: 'Truck',
  SUV: 'Sports Utility Vehicle',
  TR: 'Truck',
  M: 'Motorcycle',
  T: 'Trailer',
  O: 'Motorhome',
  CC: 'Cab & Chassis',
};

/**
 * Label a vehicle type the way the ATC request was built.
 *
 * Accepts anything the forms hold — a fees dropdown key ('Light Truck'), a docs
 * dropdown value ('Truck'), a display label ('Pickup Truck') or a raw ATC code
 * ('LT') — because `normalizeVehicleType` already resolves all four to the code
 * that goes out on the wire. All four of those resolve to LT and therefore
 * render "Truck".
 *
 * Anything that does not resolve falls through to the trimmed input rather than
 * to '' or "Unknown". The live case is "Other" on the Docs / Refi-Docs
 * dropdown, which is absent from VEHICLE_TYPE_MAPPER and therefore leaves
 * <VehicleType> blank: the row should keep saying "Other" rather than vanish
 * from the PDF, and the same fallthrough is what the bot does with an
 * unrecognised code.
 *
 * @param {string} vehicleType
 * @returns {string}
 */
export const vehicleTypeLabel = (vehicleType) => {
  const code = normalizeVehicleType(vehicleType);
  if (code && ATC_VEHICLE_TYPE_LABEL[code]) return ATC_VEHICLE_TYPE_LABEL[code];
  return String(vehicleType ?? '').trim();
};
